<template>
    <asli-form @submitted="goNext">
        <asli-form-section title="Confirm Your Details" heading-level="h2">
            <asli-date
                id="policy_start_date"
                name="policy_start_date"
                autocomplete="on"
                rules="required:start_date"
                label="When do you require cover from i.e. policy/cover start date?"
                v-model="startDate"
                :date-from="startDateFrom"
                :date-to="startDateTo"
                :show-today="false"
                @date-selected="updateAndCheckFormValues"
                @validated="updateAndCheckFormValues"
                @update:modelValue="updateAndCheckFormValues"
                calendar-aria-label="Open calendar to select policy start date"
            />
            <asli-radio
                v-if="hasVoluntaryExcess"
                id="excess"
                name="excess"
                v-model="coverDetails.voluntary_excess"
                description="Please select the amount you are willing to pay for your excess"
                :tooltipText="QUOTE_CONFIRM_HELP_TEXT.voluntary_excess"
                label="Excess"
                :options="postQuoteExcessOptions"
                @change="updateAndCheckFormValues"
            />
        </asli-form-section>
        <asli-form-section title="Additional Cover" heading-level="h2" v-if="hasAdditionalCover">
            <asli-radio
                v-if="hasFamilyLegalProtection"
                id="family_legal_protection"
                name="family_legal_protection"
                label="Would you like to add Family Legal Protection cover?"
                v-model="addons.family_legal_protection"
                :tooltipText="QUOTE_CONFIRM_HELP_TEXT.family_legal_protection"
                :options="lists?.family_legal_protection_addons"
                @change="updateAndCheckFormValues"
            />
            <asli-radio
                v-if="hasGardenCover"
                id="garden_cover"
                name="garden_cover"
                v-model="addons.garden_cover"
                label="Would you like to add cover for items in your garden?"
                :tooltipText="QUOTE_CONFIRM_HELP_TEXT.garden_cover"
                :options="lists?.garden_cover_addons"
                @change="updateAndCheckFormValues"
            />
        </asli-form-section>
        <quote-button-bar
            :quote="quote"
            next-is-submit
            :recalculate-flag="recalculateFlag"
            :is-monthly="isPayMonthly"
            :premiums="{ monthly: quote.monthly_total_payable, annual: quote.premium }"
            @go-back="goBack"
        />
    </asli-form>
</template>

<script lang="ts" setup>
import { AsliForm, AsliFormSection, AsliRadio, AsliDate } from "@mbs-npm/anpost-ui";
import { ROUTE_NAMES } from "@/router/types";
import { useRouter } from "vue-router";
import { computed, onMounted, reactive, ref } from "vue";
import { useMainStore } from "@/stores/main";
import { useProposalStore } from "@/stores/risk/risk";
import { List, useListStore } from "@/stores/lists/lists";
import { Addons, CoverDetails } from "@/stores/risk/types/types";
import dayjs from "dayjs";
import { QUOTE_CONFIRM_HELP_TEXT } from "@/utils/helpText";
import QuoteButtonBar from "@/components/ButtonBars/QuoteButtonBar.vue";
import { useDetectFormChanges } from "@/hooks/useDetectFormChanges";
import { isInsurer } from "@/utils";
import { INSURER_NAMES } from "@/static/constants";

const mainStore = useMainStore();

const proposalStore = useProposalStore();
const router = useRouter();
const listStore = useListStore();
const lists = computed(() => listStore?.lists);

const proposal = computed(() => proposalStore?.proposal);
const quote = computed(() => proposal.value?.quote);
const postQuoteQuestions = computed(() => quote.value?.post_quote_questions);

const hasVoluntaryExcess = computed(() => postQuoteQuestions.value?.has_voluntary_access);
const hasFamilyLegalProtection = computed(() => postQuoteQuestions.value?.has_family_legal_protection);
const hasGardenCover = computed(() => postQuoteQuestions.value?.has_garden_cover);

const hasAdditionalCover = computed(() => {
    return hasFamilyLegalProtection.value || hasGardenCover.value;
});

const startDateFrom = new Date();
const startDateFromDay = dayjs(startDateFrom);
const startDateTo = computed(() => startDateFromDay.add(30, "day").toDate());

const startDate = ref(proposalStore.proposal.risk.policy_details.start_date);

const getPostQuoteExcessOptions = () => {
    let excessOptions: List;
    if (isInsurer(quote.value, INSURER_NAMES.Allianz)) {
        excessOptions = lists.value.excess_options_allianz;
    } else {
        excessOptions = lists.value.excess_options_aviva;
    }
    return excessOptions.map(option => {
        return {
            value: Number(option.value),
            name: option.name
        };
    });
};

const postQuoteExcessOptions = computed(() => {
    return getPostQuoteExcessOptions();
});

const isPayMonthly = computed(() => proposal.value.is_pay_monthly);

const recalculateFlag = ref(false);

function getDefaultVoluntaryExcess() {
    const proposalExcess = Number(proposalStore.proposal.risk.cover_details.voluntary_excess);
    if (proposalExcess) {
        const selectedOption = postQuoteExcessOptions.value.find(option => option.value === proposalExcess);
        return selectedOption ? selectedOption.value : postQuoteExcessOptions.value[0].value;
    } else {
        return postQuoteExcessOptions.value[0].value;
    }
}
const coverDetails = reactive<Partial<CoverDetails>>({
    voluntary_excess: getDefaultVoluntaryExcess()
});

const addons = reactive<Addons>({
    family_legal_protection: proposal.value?.addons?.family_legal_protection ?? null,
    garden_cover: proposal.value?.addons?.garden_cover ?? null
});

function buildFormValues() {
    return {
        policy_start_date: startDate.value,
        excess: coverDetails.voluntary_excess,
        family_legal_protection: addons.family_legal_protection,
        garden_cover: addons.garden_cover
    };
}

const { checkFormHasChanges, updateFormValues, resetInitialValues, initialiseValues } = useDetectFormChanges();

onMounted(() => {
    initialiseValues(buildFormValues());
});

function updateAndCheckFormValues() {
    updateFormValues(buildFormValues());
    recalculateFlag.value = checkFormHasChanges();
}

function commitStartDate(updateValues = true) {
    if (updateValues) updateAndCheckFormValues();
    if (startDate.value && dayjs(startDate.value).isValid()) {
        proposalStore.patchPolicyDetails({
            ...proposalStore.proposal.risk.policy_details,
            start_date: startDate.value
        });
    }
}

function commitRisk() {
    updateAndCheckFormValues();
    commitStartDate(false);
    proposalStore.patchCoverDetails({
        ...coverDetails,
        voluntary_excess: hasVoluntaryExcess ? coverDetails.voluntary_excess : 0
    });
    proposalStore.patchAddons(addons);
}

function goBack() {
    router.push({
        name: ROUTE_NAMES.YourQuotes,
        query: {
            uuid: proposalStore.proposal.uuid
        }
    });
}

async function recalculateQuote() {
    await proposalStore.recalculateQuote(false, isPayMonthly.value);
    recalculateFlag.value = false;
    resetInitialValues();
}

async function goNext() {
    commitStartDate();
    commitRisk();
    if (recalculateFlag.value) {
        return await recalculateQuote();
    }
    mainStore.setLoadingState(true);
    try {
        await router.push({
            name: ROUTE_NAMES.Marketing
        });
    } finally {
        mainStore.setLoadingState(false);
    }
}
</script>
